import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FileText } from 'lucide-react';
import { api } from '../lib/api';
import VerificationBreadcrumb from '../components/VerificationBreadcrumb';

function ageLabel(value) {
  if (!value) return 'Off';
  return `${value}+`;
}

export default function SignatureNew() {
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [reference, setReference] = useState('');
  const [signatureLevel, setSignatureLevel] = useState('SES');
  const [minAge, setMinAge] = useState(0);
  const [documentFile, setDocumentFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [workspaceLocked, setWorkspaceLocked] = useState(false);
  const ageOptions = [0, 16, 18, 21].includes(Number(minAge))
    ? [0, 16, 18, 21]
    : [0, Number(minAge), 16, 18, 21].filter((value, index, values) => values.indexOf(value) === index).sort((a, b) => a - b);

  useEffect(() => {
    api.get('/organization')
      .then(({ data }) => setMinAge(data.organization?.min_age || 0))
      .catch(() => {});
  }, []);

  const submit = async (event) => {
    event.preventDefault();
    setError(null);
    setWorkspaceLocked(false);
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('title', title);
      fd.append('signer_first_name', firstName);
      fd.append('signer_last_name', lastName);
      fd.append('signer_email', email);
      fd.append('signature_level', signatureLevel);
      if (signatureLevel === 'AES') fd.append('min_age', Number(minAge));
      if (reference.trim()) fd.append('reference', reference.trim());
      if (documentFile) fd.append('document', documentFile);
      const { data } = await api.post('/signatures', fd);
      navigate(`/dashboard/signatures/${data.signature.id}`);
    } catch (e) {
      const code = e?.response?.data?.code || e?.response?.data?.error;
      const resourceType = e?.response?.data?.details?.resource_type;
      setWorkspaceLocked(code === 'workspace_verification_required');
      const messages = {
        workspace_verification_required: 'Verify your identity to activate this workspace and unlock the free tier.',
        missing_title: 'Enter a document title.',
        missing_signer_name: 'Enter the signer first and last name.',
        missing_signer_email: 'Enter the signer email address.',
        missing_document: 'Upload one PDF document.',
        invalid_document_pdf: 'Upload a valid PDF document.',
        invalid_min_age: 'Choose a valid age check option.',
        usage_limit_exhausted: resourceType === 'verification'
          ? 'No KYC verification usage available for AES. Revoke an unused link or wait until next period.'
          : 'No signature usage available. Revoke an unused link or wait until next period.',
      };
      setError(messages[code] || 'Could not create the draft document.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="signature-new-page">
      <VerificationBreadcrumb rootLabel="Documents" rootTo="/dashboard/signatures" current="New document" showDashboard={false}/>

      <div className="mx-auto max-w-3xl space-y-6">
        <div className="text-center">
          <p className="text-xs font-mono uppercase tracking-widest text-zinc-500">Document setup</p>
          <h1 className="font-display text-3xl font-bold tracking-tight mt-2">New document</h1>
          <p className="mx-auto mt-2 max-w-xl text-sm text-zinc-500">Create a draft first. Signature fields and delivery can be configured next.</p>
        </div>

        <form onSubmit={submit} className="mx-auto max-w-2xl border border-zinc-200 rounded-sm p-6">
        <h2 className="font-display text-lg font-semibold">Document</h2>
        <p className="text-sm text-zinc-500 mt-1">Prepare the document, signer, and signing flow before sending the invitation.</p>

        <label className="block text-xs uppercase tracking-widest text-zinc-500 mt-5 mb-1">Signature level</label>
        <div className="grid sm:grid-cols-2 gap-2">
          <button type="button" onClick={() => setSignatureLevel('SES')}
            className={`border rounded-sm px-3 py-3 text-left ${signatureLevel === 'SES' ? 'border-black bg-zinc-900 text-white' : 'border-zinc-200 text-zinc-700'}`}>
            <span className="block text-sm font-medium">Simple Electronic Signature</span>
            <span className={`block text-xs mt-1 ${signatureLevel === 'SES' ? 'text-zinc-300' : 'text-zinc-500'}`}>Fast signing, no KYC verification</span>
          </button>
          <button type="button" onClick={() => setSignatureLevel('AES')}
            className={`border rounded-sm px-3 py-3 text-left ${signatureLevel === 'AES' ? 'border-black bg-zinc-900 text-white' : 'border-zinc-200 text-zinc-700'}`}>
            <span className="block text-sm font-medium">Advanced Electronic Signature</span>
            <span className={`block text-xs mt-1 ${signatureLevel === 'AES' ? 'text-zinc-300' : 'text-zinc-500'}`}>KYC verification required before signing</span>
          </button>
        </div>

        {signatureLevel === 'AES' && (
          <>
            <label className="block text-xs uppercase tracking-widest text-zinc-500 mt-5 mb-1">Age check</label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {ageOptions.map((value) => (
                <button key={value} type="button" onClick={() => setMinAge(value)}
                  className={`border rounded-sm px-3 py-3 text-sm text-left ${Number(minAge) === value ? 'border-black bg-zinc-900 text-white' : 'border-zinc-200 text-zinc-700'}`}>
                  {ageLabel(value)}
                </button>
              ))}
            </div>
          </>
        )}

        <div className="mt-6 pt-5 border-t border-zinc-200">
          <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-1">Document title</label>
          <input value={title} onChange={(e) => setTitle(e.target.value)} required
            className="w-full border border-zinc-200 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-black"
            placeholder="Service agreement"/>
        </div>

        <label className="block text-xs uppercase tracking-widest text-zinc-500 mt-4 mb-1">PDF document</label>
        <input type="file" accept="application/pdf" required
          onChange={(e) => setDocumentFile(e.target.files?.[0] || null)}
          className="w-full border border-zinc-200 rounded-sm px-3 py-2 text-sm file:mr-3 file:border-0 file:bg-zinc-100 file:px-3 file:py-1 file:text-xs file:font-medium"/>
        {documentFile && (
          <p className="mt-2 text-xs text-zinc-500 font-mono inline-flex items-center gap-1">
            <FileText className="w-3.5 h-3.5" strokeWidth={1.5}/>{documentFile.name}
          </p>
        )}

        <div className="mt-6 pt-5 border-t border-zinc-200">
        <div className="grid sm:grid-cols-2 gap-3">
          <label className="block">
            <span className="block text-xs uppercase tracking-widest text-zinc-500 mb-1">Signer first name</span>
            <input value={firstName} onChange={(e) => setFirstName(e.target.value)} required
              className="w-full border border-zinc-200 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-black"
              autoComplete="given-name"/>
          </label>
          <label className="block">
            <span className="block text-xs uppercase tracking-widest text-zinc-500 mb-1">Signer last name</span>
            <input value={lastName} onChange={(e) => setLastName(e.target.value)} required
              className="w-full border border-zinc-200 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-black"
              autoComplete="family-name"/>
          </label>
        </div>

        <label className="block text-xs uppercase tracking-widest text-zinc-500 mt-4 mb-1">Signer email</label>
        <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" required
          className="w-full border border-zinc-200 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-black"/>

        <label className="block text-xs uppercase tracking-widest text-zinc-500 mt-4 mb-1">Reference (optional)</label>
        <input value={reference} onChange={(e) => setReference(e.target.value)}
          className="w-full border border-zinc-200 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-black"
          placeholder="contract_2026_001"/>
        </div>

        {error && (
          <p className="mt-3 text-sm text-red-700">
            {error}
            {workspaceLocked && (
              <> <Link to="/settings" className="font-medium underline">Go to Workspace verification</Link></>
            )}
          </p>
        )}

        <div className="mt-6 flex justify-end gap-2">
          <Link to="/dashboard/signatures" className="text-sm px-3 py-2 border border-zinc-200 rounded-sm hover:border-zinc-400">Cancel</Link>
          <button type="submit" disabled={loading || !documentFile || !title.trim() || !firstName.trim() || !lastName.trim() || !email.trim()}
            className="text-sm px-3 py-2 bg-black text-white rounded-sm hover:bg-zinc-800 disabled:opacity-50">
            {loading ? 'Saving...' : 'Save draft'}
          </button>
        </div>
        </form>
      </div>
    </div>
  );
}
